'use client';

import * as React from 'react';
import {
  SelectItem,
  SelectPopup,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/forms/Select';
import { useAIInteraction } from '@/components/organisms/ai/_shared/interaction-context';
import { validateSelectBlockSchema } from '@/components/organisms/ai/_shared/schema-validation';
import { cn, normalizeOptions } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

/**
 * Option format for AISelect
 */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Schema for configuring an AISelect
 */
export interface SelectBlockSchema {
  /** Unique identifier for the component instance */
  id: string;
  /** Label to display above the select */
  label?: string;
  /** Multiple selection is not supported; true is rejected by runtime validation. */
  multiple?: false;
  /** Options - can be string[] shorthand or full object format */
  options: Array<SelectOption> | string[];
  /** Default selected value */
  defaultValue?: string;
  /** Whether selection is required */
  required?: boolean;
  /** Placeholder text when no option is selected */
  placeholder?: string;
}

/**
 * Props for AISelect
 */
export interface AISelectProps {
  /** Schema defining the select configuration */
  schema: SelectBlockSchema;
  /** Additional class names */
  className?: string;
}

// =============================================================================
// Component
// =============================================================================

function InvalidSelectSchema({
  className,
}: {
  className?: string;
}): React.ReactElement {
  return (
    <div
      data-slot="ai-select"
      role="alert"
      className={cn(
        'rounded-lg border border-destructive bg-destructive/20 p-4 text-sm text-destructive',
        className,
      )}
    >
      Invalid select schema
    </div>
  );
}

/**
 * AISelect - An interactive select/dropdown for use in AI chat messages.
 *
 * Renders a select dropdown that emits interaction events when the user makes a selection.
 * Supports both string array shorthand and full option object format.
 */
export function AISelect({
  schema,
  className,
}: AISelectProps): React.ReactElement {
  const { onInteraction } = useAIInteraction();
  const labelId = React.useId();
  const selectSchema = React.useMemo(
    () => (validateSelectBlockSchema(schema) ? schema : null),
    [schema],
  );

  const normalizedOptions = React.useMemo(
    () => (selectSchema ? normalizeOptions(selectSchema.options) : []),
    [selectSchema],
  );

  const handleValueChange = React.useCallback(
    (value: string) => {
      onInteraction?.({
        componentId: selectSchema?.id ?? '',
        componentType: 'select',
        action: 'change',
        value,
        timestamp: Date.now(),
      });
    },
    [onInteraction, selectSchema?.id],
  );

  if (!selectSchema) {
    return <InvalidSelectSchema className={className} />;
  }

  return (
    <div
      data-slot="ai-select"
      className={cn('flex flex-col gap-1.5', className)}
    >
      {selectSchema.label && (
        <span id={labelId} className="text-sm font-medium text-foreground">
          {selectSchema.label}
          {selectSchema.required && (
            <span className="text-destructive ml-0.5" aria-hidden="true">
              *
            </span>
          )}
        </span>
      )}
      <SelectRoot
        defaultValue={
          typeof selectSchema.defaultValue === 'string'
            ? selectSchema.defaultValue
            : undefined
        }
        onValueChange={handleValueChange}
      >
        <SelectTrigger
          aria-labelledby={selectSchema.label ? labelId : undefined}
          aria-label={
            !selectSchema.label
              ? (selectSchema.placeholder ?? 'Select an option')
              : undefined
          }
          aria-required={selectSchema.required}
        >
          <SelectValue placeholder={selectSchema.placeholder} />
        </SelectTrigger>
        <SelectPopup>
          {normalizedOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectPopup>
      </SelectRoot>
    </div>
  );
}

AISelect.displayName = 'AISelect';

export default AISelect;
